import { useEffect, useRef, useState } from "react";
import type { Restaurant } from "../game/restaurants";
import type { Settings } from "../game/settings";
import { clockTime, shiftResult, type ShiftResult } from "../game/shift";
import { Engine, type HudSnapshot } from "../play/Engine";
import { Briefing, Overlay } from "./Briefing";
import { Hud } from "./Hud";
import { SettingsPanel } from "./SettingsPanel";
import { TouchControls } from "./TouchControls";

type Phase = "briefing" | "running" | "paused";

function isTouch(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

export function ShiftScreen({
  restaurant,
  seed,
  settings,
  onSettings,
  onFinish,
  onQuit,
}: {
  restaurant: Restaurant;
  seed: number;
  settings: Settings;
  onSettings: (s: Settings) => void;
  onFinish: (result: ShiftResult) => void;
  onQuit: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [engine, setEngine] = useState<Engine | null>(null);
  const [failed, setFailed] = useState(false);
  const [hud, setHud] = useState<HudSnapshot | null>(null);
  const [phase, setPhase] = useState<Phase>("briefing");
  const [touch] = useState(isTouch);
  // The engine starts with the settings as they are; changes are applied below.
  const settingsAtStart = useRef(settings);

  useEffect(() => {
    let e: Engine;
    try {
      e = new Engine(canvasRef.current!, restaurant, seed, {
        onHud: setHud,
        onUnlock: () => setPhase((p) => (p === "running" ? "paused" : p)),
      }, settingsAtStart.current);
    } catch {
      setFailed(true);
      return;
    }
    setEngine(e);
    return () => e.dispose();
  }, [restaurant, seed]);

  useEffect(() => engine?.setRunning(phase === "running"), [engine, phase]);
  useEffect(() => engine?.applySettings(settings), [engine, settings]);

  // Switching away pauses: nobody wants to come back to a walkout.
  useEffect(() => {
    const onHide = () => {
      if (document.hidden) setPhase((p) => (p === "running" ? "paused" : p));
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat || phase === "briefing") return;
      if (e.code === "KeyP" || e.code === "Escape") {
        e.preventDefault();
        if (phase === "paused") resume();
        else setPhase("paused");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const resume = () => {
    setPhase("running");
    // Sound can only start from a click or key press, and this is one.
    engine?.sfx.unlock();
    // A click on Open the doors or Back to work counts as the gesture that takes the mouse.
    if (!touch) engine?.controls.lock();
  };

  const state = hud?.state;
  return (
    <div className="fixed inset-0 overflow-hidden bg-ink select-none">
      <canvas ref={canvasRef} className="absolute inset-0 block h-full w-full" aria-label={`The dish pit at ${restaurant.name}`} />
      {state && (
        <Hud name={restaurant.name} state={state} hint={phase === "running" ? hud.hint : null} touch={touch} onPause={() => setPhase("paused")} />
      )}
      {touch && engine && phase === "running" && !state?.over && <TouchControls controls={engine.controls} hint={hud?.hint ?? null} />}
      {!touch && phase === "running" && hud?.needsClick && (
        <p className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-ink/80 px-4 py-2 text-sm font-bold text-panel">
          Click the kitchen to look around
        </p>
      )}

      {failed && (
        <Overlay label="Can't start the kitchen">
          <h2 className="text-2xl font-black">This browser can't draw the kitchen</h2>
          <p className="mt-2 text-ink-soft">Order Up! needs WebGL. Try a recent Chrome, Safari, Firefox or Edge, or turn on hardware acceleration.</p>
          <button type="button" onClick={onQuit} className="mt-5 w-full rounded-xl bg-accent px-4 py-2 font-bold text-panel">
            Back to the restaurants
          </button>
        </Overlay>
      )}
      {!failed && phase === "briefing" && <Briefing restaurant={restaurant} touch={touch} onStart={resume} onBack={onQuit} />}
      {phase === "paused" && !state?.over && (
        <Overlay label="Paused">
          <h2 className="text-2xl font-black">On break</h2>
          <p className="mt-1 text-ink-soft">The clock's stopped. The dishes will wait.</p>
          <div className="mt-4 rounded-xl bg-tile p-3">
            <SettingsPanel settings={settings} onChange={onSettings} />
          </div>
          <div className="mt-5 flex gap-2">
            <button type="button" onClick={onQuit} className="rounded-xl border-2 border-steel px-4 py-2 font-bold hover:bg-tile">
              Walk off the job
            </button>
            <button type="button" autoFocus onClick={resume} className="flex-1 rounded-xl bg-accent px-4 py-2 font-bold text-panel shadow hover:brightness-110">
              Back to work
            </button>
          </div>
        </Overlay>
      )}
      {state?.over && (
        <Overlay label="Closed up">
          <p className="text-sm font-bold tracking-wide text-accent uppercase">{clockTime(state.setup, state.t)}</p>
          <h2 className="text-2xl font-black">All closed up!</h2>
          <p className="mt-1 text-ink-soft">Every dish is clean and on the shelf. Hang up your apron.</p>
          <button
            type="button"
            autoFocus
            onClick={() => onFinish(shiftResult(state))}
            className="mt-5 w-full rounded-xl bg-accent px-4 py-2 font-bold text-panel shadow hover:brightness-110"
          >
            Close up
          </button>
        </Overlay>
      )}
    </div>
  );
}
